import { Typography } from '@chronosphereio/chrono-ui';
import { forwardRef } from 'react';
import { isDevelopmentMode } from '@/core/utils/env';
import { GetDataResponse } from '@/plugin-system/model/queries';
import { EmptyDataQueriesProvider, StaticDataQueriesProvider } from '@/plugin-system/runtime/DataQueriesProvider';
import { useHasDataQueriesContext } from '@/plugin-system/runtime/DataQueriesProvider/DataQueriesProvider';
import { useHasPluginRegistry } from '@/plugin-system/runtime/plugin-registry';
import { VizContainerBody } from '../body/VizContainerBody';
import { VizContainerHeader } from '../header/VizContainerHeader';
import { VizContainerHeaderBase } from '../header/VizContainerHeaderBase';
import { UseVizContainerDataQueriesLoadingProps, useVizContainerDataQueriesLoading } from '../hooks';
import { VizContainerNoticesBase } from '../notices/VizContainerNoticesBase';
import { VizContainerRootBaseProps } from './model';
import { VizContainerRootBase } from './VizContainerRootBase';

export type VizContainerData = 'DataQueriesProvider' | 'none' | GetDataResponse;

export interface VizContainerRootProps
  extends Omit<VizContainerRootBaseProps, 'isLoading' | 'isFetching'>,
    UseVizContainerDataQueriesLoadingProps {
  /**
   * Prop that determines how data will be set up for the `VizContainerRoot`. It supports the following:
   * - `'DataQueriesProvider'` (default): When explicitly set to this value or `undefined`, it is expected that
   *   `VizContainer.Root` is nested inside a `DataQueriesProvider` that provides data.
   * - `'none'`: Communicates that this use case does not need data. This is equivalent to wrapping the component in a
   *   `DataQueriesProvider` with `queries` set to an empty array.
   * - `GetDataResponse`: Static data response to use. This is equivalent to wrapping the component in a
   *   `DataQueriesProvider` with `queries` set to a simple query function that returns the data response. Note that you
   *   are responsible for managing the loading/fetching states yourself if they are needed. In those cases, it may be
   *   more appropriate to set up a `DataQueriesProvider` with a custom query function. Use cases that use this data
   *   setting are unlikely to be compatible with "Add to dashboard" and "Add to notebook." Make sure the data is
   *   memoized to avoid unnecessary work being done to update the data on every render cycle.
   */
  data?: VizContainerData;
}

/**
 * Hook that determines if the `data` is not set up properly when in dev mode. When this returns true, a helpful error
 * message is shown to the developer to guide them in the right direction.
 */
const useShowMisconfiguredDataHelp = ({ data }: Pick<Required<VizContainerRootProps>, 'data'>): boolean => {
  const hasDataQueriesContext = useHasDataQueriesContext();
  return isDevelopmentMode() && data === 'DataQueriesProvider' && !hasDataQueriesContext;
};

/**
 * Hook that determines if the viz-framework context providers are not set up properly when in dev mode. When this
 * returns true, a helpful error message is shown to the developer to guide them in the right direction.
 */
const useShowMissingVisualizationProviderHelp = () => {
  // Checking for the plugin registry is a reasonable stand-in for if someone probably missed the
  // `VisualizationProvider` wrapper from `cloud` because that particular context provider is almost never used by
  // itself.
  const hasPluginRegistry = useHasPluginRegistry();
  return isDevelopmentMode() && !hasPluginRegistry;
};

/**
 * Root component that contains all the parts of a `VizContainer`. It extends from the chrono-ui `Container` component
 * and shares similar props and behavior, when possible, for consistency.
 *
 * This is the "enhanced" version of the root component that automatically comes with built-in features which require
 * configuration of parent context providers. Instead use `RootBase` if you want a much simpler root component. Built-in
 * features currently include:
 * - Automatically setting loading/fetching state based on the results from data queries. This feature depends on
 *   being nested within a `DataQueriesProvider`.
 */
export const VizContainerRoot = forwardRef<HTMLDivElement, VizContainerRootProps>(function VizContainerRoot(
  { data = 'DataQueriesProvider', ...props }: VizContainerRootProps,
  ref
) {
  const showMissingProvidersHelp = useShowMissingVisualizationProviderHelp();
  const showMisconfiguredDataHelp = useShowMisconfiguredDataHelp({ data });

  if (showMissingProvidersHelp) {
    return <MissingVisualizationProviderMessage />;
  }

  if (showMisconfiguredDataHelp) {
    // When in development mode, if the data is set to `DataQueriesProvider` (common because it's the default) but the
    // `DataQueriesProvider` is missing, wrap the `VizContainer` in an empty `DataQueriesProvider` and replace the
    // content with a helpful error message explaining how to fix the data. This avoids throwing a generic error
    // about a missing context provider, and instead provides more helpful guidance.
    return (
      <EmptyDataQueriesProvider>
        <VizContainerRootWithDataQueries {...props} ref={ref}>
          <VizContainerHeader
            title="VizContainer data is misconfigured"
            errors={{
              errorState: 'partial',
              errors: [
                {
                  count: 1,
                  errorMessage:
                    "This VizContainer's data is misconfigured. See the message in the body for guidance on how to fix it!",
                },
              ],
            }}
          />
          <VizContainerBody>
            <MissingDataQueryProviderMessage />
          </VizContainerBody>
        </VizContainerRootWithDataQueries>
      </EmptyDataQueriesProvider>
    );
  }

  const vizContainerContent = <VizContainerRootWithDataQueries {...props} ref={ref} />;

  if (data === 'DataQueriesProvider') {
    // The `VizContainer` is wrapped in a `DataQueriesProvider`. No special handling is needed. This is the default, so
    // this case is also hit when the consumer does not set `data`.
    return vizContainerContent;
  }

  if (data === 'none') {
    // Wrap the `VizContainer` in an empty `DataQueriesProvider` to avoid missing context errors.
    return <EmptyDataQueriesProvider>{vizContainerContent}</EmptyDataQueriesProvider>;
  }

  // Data is a `GetDataResponse`. Wrap the `VizContainer` in a helper that sets up a `DataQueriesProvider` that
  // returns that data.
  return <StaticDataQueriesProvider data={data}>{vizContainerContent}</StaticDataQueriesProvider>;
});

type VizContainerRootWithDataQueriesProps = Omit<VizContainerRootProps, 'data'>;

/**
 * Internal implementation detail of `VizContainerRoot` that assumes a `DataQueryProvider` has been set up. This
 * needs to be a separate component, so that we can handle some custom `DataQueryProvider` wrapping in
 * `VizContainerRoot` to improve dev ergonomics.
 */
const VizContainerRootWithDataQueries = forwardRef<HTMLDivElement, VizContainerRootWithDataQueriesProps>(
  function VizContainerRootWithDataQueries(
    { isLoading: isLoadingProp, isFetching: isFetchingProp, children, ...containerProps },
    ref
  ) {
    const { isLoading, isFetching } = useVizContainerDataQueriesLoading({
      isFetching: isFetchingProp,
      isLoading: isLoadingProp,
    });

    return (
      <VizContainerRootBase isLoading={isLoading} isFetching={isFetching} {...containerProps} ref={ref}>
        {children}
      </VizContainerRootBase>
    );
  }
);

function MissingVisualizationProviderMessage() {
  // We need to use the Base components here because many of the non-base components will throw if some of the
  // context providers are missing.
  return (
    <VizContainerRootBase>
      <VizContainerHeaderBase>
        <VizContainerNoticesBase
          errors={[
            {
              count: 1,
              errorMessage:
                'This VizContainer will not work because of missing context providers. See the message in the body for guidance on how to fix it!',
            },
          ]}
        />
        <Typography component="div" variant="inherit" noWrap>
          Missing viz-framework providers
        </Typography>
      </VizContainerHeaderBase>
      <VizContainerBody>
        <Typography component="div" sx={{ color: (theme) => theme.palette.error.main }}>
          <p>
            This <code>VizContainer</code> is missing critical context providers from <code>viz-framework</code>. In{' '}
            <code>cloud</code>, you can resolve this by ensuring the component has an ancestor{' '}
            <code>VisualizationProvider</code>. <code>VisualizationProvider</code> is usually configured on a
            page/feature level and sets up the many context providers needed for <code>viz-framework</code> features to
            work.
          </p>
        </Typography>
      </VizContainerBody>
    </VizContainerRootBase>
  );
}

function MissingDataQueryProviderMessage() {
  return (
    <Typography component="div" sx={{ color: (theme) => theme.palette.error.main }}>
      <p>
        This <code>VizContainer</code> is configured to get data from a <code>DataQueriesProvider</code>, but is not
        currently wrapped in one. You can resolve this by:
      </p>
      <ul>
        <li>
          Wrapping the component in a <code>DataQueriesProvider</code>.
        </li>
        <li>
          Setting the <code>data</code> prop on <code>VizContainer.Root</code> to <code>&apos;none&apos;</code>.
        </li>
        <li>
          Setting the <code>data</code> prop on <code>VizContainer.Root</code> to a <code>GetDataResponse</code> object.
        </li>
      </ul>
    </Typography>
  );
}
